import { Button, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import type { ChangeEvent, KeyboardEvent } from "react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { arraysHaveSameItems } from "@entrilia/shared/utilities/arrayHelper";
import biApi from "../../../../../../../../../api/biApi";
import type { MeasureSubgroup } from "../../../../../../../../../api/types/svTypes.ts";
import { selectAllMeasures } from "../../../../../store/selectors/metaDataSelectors";
import { createMetaDataAction } from "../../../../../store/utils/storeUtils";
import { AlertItem } from "../../../../common/AlertItem";
import {
  getSubgroupMeasureOptions,
  getSubgroupMemberIds,
  validateSubgroupName,
  validateSubgroupRename,
} from "../utilities/measureSubgroups";
import SubgroupMeasuresSelect from "./SubgroupMeasuresSelect";

const createMeasureSubgroup = withErrorHandling(biApi.createMeasureSubgroup);
const updateMeasureSubgroup = withErrorHandling(biApi.updateMeasureSubgroup);

interface CreateSubgroupProps {
  group: string;
  subgroup?: undefined;
  existingSubgroups: MeasureSubgroup[];
  onClose: () => void;
}

interface EditSubgroupProps {
  group?: undefined;
  subgroup: MeasureSubgroup;
  existingSubgroups: MeasureSubgroup[];
  onClose: () => void;
}

type Props = CreateSubgroupProps | EditSubgroupProps;

const SubgroupDialog = ({ group, subgroup, existingSubgroups, onClose }: Props) => {
  const dispatch = useDispatch();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const allMeasures = useSelector(selectAllMeasures);

  const [name, setName] = useState(subgroup?.name ?? "");
  const [selectedMeasureIds, setSelectedMeasureIds] = useState(() =>
    subgroup ? getSubgroupMemberIds(getSubgroupMeasureOptions(allMeasures, subgroup.group), subgroup.id) : []
  );
  const [nameServerError, setNameServerError] = useState<string>();
  const [savingError, setSavingError] = useState<string>();
  const [saving, setSaving] = useState(false);

  const measureOptions = getSubgroupMeasureOptions(allMeasures, subgroup ? subgroup.group : group);
  const memberIds = subgroup ? getSubgroupMemberIds(measureOptions, subgroup.id) : [];

  const trimmedName = name.trim();
  const validationError = subgroup
    ? validateSubgroupRename(name, subgroup, existingSubgroups)
    : validateSubgroupName(name, existingSubgroups);
  const nameFieldError = nameServerError ?? (trimmedName ? validationError : undefined);
  const hasChanges =
    subgroup === undefined || trimmedName !== subgroup.name || !arraysHaveSameItems(selectedMeasureIds, memberIds);
  const canSave = hasChanges && validationError === undefined && nameServerError === undefined && !saving;
  const operationName = subgroup ? "updateMeasureSubgroup" : "createMeasureSubgroup";

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
    setNameServerError(undefined);
    setSavingError(undefined);
  };

  const handleMeasuresChange = (measureIds: string[]) => {
    setSelectedMeasureIds(measureIds);
    setSavingError(undefined);
  };

  const handleSave = async () => {
    if (!canSave) {
      return;
    }
    setSaving(true);
    setSavingError(undefined);
    const [response, error] = subgroup
      ? await updateMeasureSubgroup(subgroup.id, { name: trimmedName, measureIds: selectedMeasureIds })
      : await createMeasureSubgroup({ group, name: trimmedName, measureIds: selectedMeasureIds });
    setSaving(false);

    if (error) {
      if (error.type === "AlreadyExist") {
        setNameServerError(error.message);
        return;
      }
      logError(error, `[SubgroupDialog] ${operationName}`);
      if (subgroup && error.type === "NotFound") {
        sendNotificationError(error.message);
        dispatch(createMetaDataAction("deleteMeasureSubgroup", subgroup.id));
        onClose();
        return;
      }
      setSavingError(error.message);
      return;
    }

    if (subgroup) {
      dispatch(createMetaDataAction("renameMeasureSubgroup", response.subgroup));
    } else {
      dispatch(createMetaDataAction("addMeasureSubgroup", response.subgroup));
    }
    response.measures.forEach((measure) => dispatch(createMetaDataAction("updateMeasure", measure)));
    sendNotification(subgroup ? "Changes saved" : "New subgroup created successfully");
    onClose();
  };

  const handleNameKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      void handleSave();
    }
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>{subgroup ? "Edit Subgroup" : "New Subgroup"}</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2}>
          {savingError && <AlertItem error={savingError} />}
          <TextField
            autoFocus
            fullWidth
            label="Name"
            value={name}
            error={nameFieldError !== undefined}
            helperText={nameFieldError}
            onChange={handleNameChange}
            onKeyDown={handleNameKeyDown}
          />
          <Typography color="textSecondary">Select the Custom measures you want to move to this subgroup.</Typography>
          <SubgroupMeasuresSelect
            options={measureOptions}
            selectedMeasureIds={selectedMeasureIds}
            disabled={saving}
            onChange={handleMeasuresChange}
          />
        </Stack>
      </DialogContent>
      <DialogButtons>
        <Button variant="text" color="secondary" disabled={saving} onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!canSave} loading={saving} onClick={handleSave}>
          Save
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default SubgroupDialog;
